import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

/**
 * The new customer navigation (prototype v6) is ON for a company only when ALL of these hold:
 *   - the company's `nav_v6` flag is true (set by the admin from the company edit form; delivered in
 *     GET /company/details as `company.nav_v6`), or, in NON-production builds only, the self-test override
 *     `localStorage.pp_nav_v6 === '1'`;
 *   - the account is NOT a restaurant (restaurant accounts keep their own navigation untouched).
 * Default is OFF for everyone. Turning it off in the admin brings today's UI back on the next focus refetch.
 *
 * The override reads `process.env.REACT_APP_APP_ENV`, which CRA inlines at build time: in the PROD bundle the
 * whole `&&` branch is a constant `false` and is removed, so a stored flag has no effect there (the PROD
 * bundle is checked for the string in the tests/proof).
 */
export function computeNavV6(details) {
  if (!details || typeof details !== 'object') return false;
  if (details.company?.account_type === 'restaurant') return false;
  if (details.company?.nav_v6 === true) return true;
  return process.env.REACT_APP_APP_ENV !== 'Production' && devOverrideOn();
}

function devOverrideOn() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('pp_nav_v6') === '1';
  } catch (_) {
    return false;
  }
}

/** Same source order as useEntitlements: the router's cached details first, then redux. */
export function useNavV6() {
  const redux = useSelector((s) => s.global?.companyDetails);
  return computeNavV6(useCachedDetails() ?? redux ?? null);
}

// Layout renders on every page (some test trees have no QueryClientProvider): read the cache when there is one.
function useCachedDetails() {
  try {
    return useQueryClient().getQueryData('getUserSubscription');
  } catch (_) {
    return undefined;
  }
}

export default useNavV6;
